import type {
  GiftcardTheme,
  GiftcardThemeDesign,
  GiftcardThemeDesignShowcase,
  GiftcardThemeShowcase,
} from '~/composables/types/giftcardTheme'

const mediaUrl = (media?: {
  url: string
  cropped?: { url: string | null } | null
}) => media?.cropped?.url || media?.url || undefined

const toDesign = (
  showcase: GiftcardThemeDesignShowcase
): GiftcardThemeDesign => ({
  id: showcase.id,
  name: showcase.additional_data?.name ?? '',
  sampleTitle: showcase.title ?? '',
  sampleMessage: showcase.subtitle ?? '',
  swatchImage: mediaUrl(showcase.images?.data?.swatch),
  backgroundImage: mediaUrl(showcase.images?.data?.background),
  backgroundColor: showcase.additional_data?.pdf_background_color || undefined,
})

/** URL-safe theme name: "Fußball & Fans" -> "fussball-fans" */
export const giftcardThemeSlug = (name: string) =>
  name
    .toLowerCase()
    .replace(/ß/g, 'ss')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')

/**
 * Map the themes from `GET /v1/store/gift-card/themes` (each holding its
 * designs as `children`), kept in API order. A theme's slug is its route param
 * (`/giftcard/theme/[theme]`), so themes without a name or without designs are
 * left out rather than linking to an empty page.
 */
export const mapGiftcardThemes = (
  showcases: GiftcardThemeShowcase[]
): GiftcardTheme[] =>
  showcases
    .map((showcase) => {
      const name = showcase.title?.trim() ?? ''
      return {
        id: showcase.id,
        name,
        slug: giftcardThemeSlug(name),
        thumbnailImage: mediaUrl(showcase.images?.data?.thumbnail),
        designs: (showcase.children?.data ?? []).map(toDesign),
      }
    })
    .filter((theme) => theme.slug && theme.designs.length)
